import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useBrandContext } from "@/contexts/brand-context";
import { useDirectUpload } from "@/hooks/use-calendar";
import { Button } from "@/components/ui/button";
import { LibraryPickerModal } from "@/components/media/library-picker-modal";
import { assetToMediaKind } from "@/hooks/use-brand-assets";
import { useNavigate, useParams } from "react-router-dom";
import { Upload, Sparkles, CheckCircle2, ArrowLeft } from "lucide-react";
import { apiFetch } from "@/lib/api-client";
import { optimizeImageForUpload, describeOptimization } from "@/lib/image-optimize";
import { toast } from "sonner";

type Step = "upload" | "configure" | "confirm";

export function BrandCalendarUploadPage() {
  const { brand } = useBrandContext();
  const navigate = useNavigate();
  const { brandSlug } = useParams<{ brandSlug: string }>();
  const { t } = useTranslation("calendar");

  const uploadMut = useDirectUpload(brand.id);

  const [step, setStep] = useState<Step>("upload");
  const [mode, setMode] = useState<"ai" | "manual">("ai");
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadId, setUploadId] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) setUploadedFile(file);
  };

  const handleUpload = async () => {
    if (!uploadedFile) return;

    const mediaType = uploadedFile.type.startsWith("video/") ? "video" : "image";

    // Optimize image client-side before upload to minimise IG re-encoding loss.
    const { file: toUpload, report } = await optimizeImageForUpload(uploadedFile);
    const note = describeOptimization(report);
    if (note) toast.success(note);

    // Get presigned URL from API
    const presigned = await apiFetch<{ uploadUrl: string; publicUrl: string }>(
      `/brands/${brand.id}/upload-url?filename=${encodeURIComponent(toUpload.name)}&contentType=${encodeURIComponent(toUpload.type)}`
    );

    // Upload directly to S3
    await fetch(presigned.uploadUrl, {
      method: "PUT",
      body: toUpload,
      headers: { "Content-Type": toUpload.type },
    });

    uploadMut.mutate(
      {
        mediaUrls: [presigned.publicUrl],
        mediaType,
        originalName: uploadedFile.name,
      },
      {
        onSuccess: (data) => {
          setUploadId(data.id);
          setStep("configure");
        },
      },
    );
  };

  const handleConfirm = () => {
    setStep("confirm");
  };

  const steps: Step[] = ["upload", "configure", "confirm"];

  return (
    <div className="max-w-lg mx-auto py-8 space-y-6">
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={() => navigate(`/${brandSlug}/calendar`)}
        >
          <ArrowLeft size={14} />
        </Button>
        <h1 className="text-xl font-bold">{t("upload.title")}</h1>
      </div>

      {/* Progress */}
      <div className="flex items-center gap-2 text-xs">
        {steps.map((s, i) => (
          <div key={s} className="flex items-center gap-1">
            <div
              className={`h-6 w-6 rounded-full flex items-center justify-center text-xs ${
                step === s ? "bg-primary text-primary-foreground" :
                i < steps.indexOf(step) ? "bg-green-500 text-white" :
                "bg-muted text-muted-foreground"
              }`}
            >
              {i + 1}
            </div>
            <span className={step === s ? "font-medium" : "text-muted-foreground"}>
              {t(`upload.steps.${s}`)}
            </span>
            {i < 2 && <span className="text-muted-foreground mx-1">→</span>}
          </div>
        ))}
      </div>

      {/* Step 1: Upload */}
      {step === "upload" && (
        <div className="space-y-4">
          <div className="border-2 border-dashed rounded-lg p-8 text-center">
            {uploadedFile ? (
              <div className="space-y-2">
                <div className="text-2xl">{uploadedFile.type.startsWith("video/") ? t("queue.video") : t("queue.image")}</div>
                <div className="text-sm font-medium">{uploadedFile.name}</div>
                <div className="text-xs text-muted-foreground">
                  {t("upload.fileSizeMb", { size: (uploadedFile.size / 1024 / 1024).toFixed(1) })}
                </div>
              </div>
            ) : (
              <label className="cursor-pointer space-y-2 block">
                <Upload size={32} className="mx-auto text-muted-foreground" />
                <div className="text-sm">{t("upload.dropZone")}</div>
                <div className="text-xs text-muted-foreground">{t("upload.supports")}</div>
                <input
                  type="file"
                  accept="image/*,video/*"
                  className="hidden"
                  onChange={handleFileChange}
                />
              </label>
            )}
          </div>

          {!uploadedFile && (
            <div className="flex justify-center">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setPickerOpen(true)}
                disabled={uploadMut.isPending}
              >
                {t("common:library.browse")}
              </Button>
            </div>
          )}

          <div className="space-y-2">
            <label className="flex items-center gap-2 cursor-pointer p-3 rounded-md border hover:bg-accent/50">
              <input
                type="radio"
                checked={mode === "ai"}
                onChange={() => setMode("ai")}
              />
              <Sparkles size={16} className="text-yellow-500" />
              <div>
                <div className="text-sm font-medium">{t("upload.modeAi.label")}</div>
                <div className="text-xs text-muted-foreground">{t("upload.modeAi.description")}</div>
              </div>
            </label>
            <label className="flex items-center gap-2 cursor-pointer p-3 rounded-md border hover:bg-accent/50">
              <input
                type="radio"
                checked={mode === "manual"}
                onChange={() => setMode("manual")}
              />
              <div>
                <div className="text-sm font-medium">{t("upload.modeManual.label")}</div>
                <div className="text-xs text-muted-foreground">{t("upload.modeManual.description")}</div>
              </div>
            </label>
          </div>

          <Button
            className="w-full"
            disabled={!uploadedFile || uploadMut.isPending}
            onClick={handleUpload}
          >
            {uploadMut.isPending ? t("upload.uploading") : t("upload.continue")}
          </Button>
        </div>
      )}

      {/* Step 2: Configure */}
      {step === "configure" && (
        <div className="space-y-4">
          {mode === "ai" ? (
            <div className="rounded-md border p-4 space-y-3">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Sparkles size={14} className="text-yellow-500" />
                {t("upload.aiSuggestions.title")}
              </div>
              <p className="text-xs text-muted-foreground">
                {t("upload.aiSuggestions.analyzed")}
              </p>
              <div className="text-sm text-muted-foreground italic">
                {t("upload.aiSuggestions.placeholder")}
              </div>
            </div>
          ) : (
            <div className="text-xs text-muted-foreground">
              {t("upload.manualHint")}
            </div>
          )}

          <div className="flex justify-between gap-2">
            <Button variant="outline" onClick={() => setStep("upload")}>{t("upload.back")}</Button>
            <Button onClick={handleConfirm}>{t("upload.confirmSchedule")}</Button>
          </div>
        </div>
      )}

      {/* Step 3: Confirm */}
      {step === "confirm" && (
        <div className="text-center space-y-4 py-8">
          <CheckCircle2 size={48} className="mx-auto text-green-500" />
          <h2 className="text-lg font-semibold">{t("upload.success.title")}</h2>
          <p className="text-sm text-muted-foreground">
            {t("upload.success.description")}
          </p>
          <div className="flex justify-center gap-3">
            <Button variant="outline" onClick={() => navigate(`/${brandSlug}/calendar`)}>
              {t("upload.success.viewCalendar")}
            </Button>
            <Button onClick={() => { setStep("upload"); setUploadedFile(null); setUploadId(null); }}>
              {t("upload.success.uploadAnother")}
            </Button>
          </div>
        </div>
      )}

      <LibraryPickerModal
        isOpen={pickerOpen}
        onClose={() => setPickerOpen(false)}
        brandId={brand.id}
        multi={false}
        onConfirm={(_urls, assets) => {
          const a = assets[0];
          if (!a) return;
          const kind = assetToMediaKind(a.type);
          if (!kind) {
            toast.error(t("common:library.onlyImagesVideos"));
            return;
          }
          uploadMut.mutate(
            {
              mediaUrls: [a.url],
              mediaType: kind,
              originalName: a.filename,
            },
            {
              onSuccess: (data) => {
                setUploadId(data.id);
                setStep("configure");
              },
            },
          );
        }}
      />
    </div>
  );
}
